import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ErrorMessage from "../Components/ErrorMessage";
import { ReactComponent as IconCrossRound } from "../Images/icon_cross_round.svg";
import { ReactComponent as RightBracket } from "../Images/right_bracket.svg";
import LayoutModal from "../Layout/LayoutModal";
import "../Styles/component.css";
import "../Styles/fonts.css";
import "../Styles/global.css";
import { postJson } from "../Utils/apiUtils";
import { showToast } from "../Utils/variables";

const PASSWORD_ERROR_MESSAGE =
  "비밀번호는 8~16자리(영문/숫자/특수문자)로 입력할 수 있어요.";
const PASSWORD_REGEX =
  /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[$`~!@$!%*#^?&\\()\-_=+]).{8,16}$/;

const ResetPasswordBox = ({ handleClickBack, handleClickX }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [newPassword2Error, setNewPassword2Error] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearErrors = () => {
    setPasswordError("");
    setNewPasswordError("");
    setNewPassword2Error("");
    setFormError("");
  };

  const validateNewPassword = (value) => PASSWORD_REGEX.test(value);

  const validateForm = () => {
    let hasValidationError = false;

    clearErrors();

    if (!password) {
      setPasswordError("현재 비밀번호를 입력해주세요.");
      hasValidationError = true;
    }

    if (!newPassword) {
      setNewPasswordError("신규 비밀번호를 입력해주세요.");
      hasValidationError = true;
    } else if (!validateNewPassword(newPassword)) {
      setNewPasswordError(PASSWORD_ERROR_MESSAGE);
      hasValidationError = true;
    }

    if (!newPassword2) {
      setNewPassword2Error("신규 비밀번호를 다시 입력해주세요.");
      hasValidationError = true;
    } else if (newPassword && newPassword !== newPassword2) {
      setNewPassword2Error(
        "비밀번호가 서로 일치하지 않습니다. 다시 한번 입력해 주세요."
      );
      hasValidationError = true;
    }

    return hasValidationError;
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleUpdatePassword();
    }
  };

  const handleUpdatePassword = async () => {
    if (isSubmitting || validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const body = { password, newPassword, newPassword2 };
      const res = await postJson("/company/updatePassword", body, navigate);

      clearErrors();

      if (res.code === 200) {
        setPassword("");
        setNewPassword("");
        setNewPassword2("");
        showToast("비밀번호 변경이 완료되었습니다.", "success");
        handleClickX();
        return;
      }

      if (res.code === 1002) {
        setFormError("파트너스 정보가 없습니다.");
      } else if (res.code === 1007) {
        setNewPassword2Error(
          "비밀번호가 서로 일치하지 않습니다. 다시 한번 입력해 주세요."
        );
      } else if (res.code === 1008 || res.code === 1009) {
        setNewPasswordError(PASSWORD_ERROR_MESSAGE);
      } else if (res.code === 1010) {
        setPasswordError(
          "비밀번호가 일치하지 않습니다. 입력한 정보를 확인해주세요."
        );
      } else {
        const fallbackMessage =
          res.message ?? "비밀번호 변경 중 오류가 발생했습니다.";
        setFormError(fallbackMessage);
        showToast(fallbackMessage, "error");
      }
    } catch (error) {
      console.error("비밀번호 재설정 중 에러:", error);
      setFormError("비밀번호 변경 중 오류가 발생했습니다.");
      showToast("비밀번호 변경 중 오류가 발생했습니다.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <div className="hrz-wrapper">
        <RightBracket
          stroke="#b0b0b0"
          width="32px"
          height="32px"
          style={{ transform: "rotate(180deg)", cursor: "pointer" }}
          onClick={handleClickBack}
        />
        <p className="bold-text" style={{ fontSize: "24px" }}>
          비밀번호 재설정
        </p>
        <IconCrossRound
          style={{ marginLeft: "auto", cursor: "pointer" }}
          onClick={handleClickX}
        />
      </div>

      <div className="hrz-wrapper" style={{ marginTop: "30px" }}>
        <p>개인정보를 보호하고 개인정보도용의 피해를 예방하기 위해</p>
        <p className="medium-text" style={{ color: "#1167ed" }}>
          &nbsp;6개월마다
        </p>
      </div>
      <p>주기적으로 비밀번호 변경을 권장하고 있습니다.</p>
      <p>비밀번호는 영문, 숫자, 특수문자를 포함한 8~16자리여야 합니다. </p>

      <div style={{ height: "30px" }} />

      <InputContainer $hasError={Boolean(passwordError)}>
        <input
          type="password"
          value={password}
          placeholder="현재 비밀번호"
          autoComplete="current-password"
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError || formError) {
              setPasswordError("");
              setFormError("");
            }
          }}
          onKeyDown={handleInputKeyDown}
        />
      </InputContainer>
      {passwordError && <ErrorMessage message={passwordError} />}
      <div style={{ height: "10px" }} />
      <InputContainer $hasError={Boolean(newPasswordError)}>
        <input
          type="password"
          value={newPassword}
          placeholder="신규 비밀번호(영문/숫자/특수문자 8~16자리)"
          autoComplete="new-password"
          onChange={(e) => {
            setNewPassword(e.target.value);
            if (newPasswordError || newPassword2Error || formError) {
              setNewPasswordError("");
              setNewPassword2Error("");
              setFormError("");
            }
          }}
          onKeyDown={handleInputKeyDown}
        />
      </InputContainer>
      {newPasswordError && <ErrorMessage message={newPasswordError} />}
      <div style={{ height: "10px" }} />
      <InputContainer $hasError={Boolean(newPassword2Error)}>
        <input
          type="password"
          value={newPassword2}
          placeholder="신규 비밀번호 재입력"
          autoComplete="new-password"
          onChange={(e) => {
            setNewPassword2(e.target.value);
            if (newPassword2Error || formError) {
              setNewPassword2Error("");
              setFormError("");
            }
          }}
          onKeyDown={handleInputKeyDown}
        />
      </InputContainer>
      {newPassword2Error && <ErrorMessage message={newPassword2Error} />}
      {formError && <ErrorMessage message={formError} />}

      <div
        className="blueBtn-base center"
        style={{
          width: "500px",
          height: "56px",
          borderRadius: "10px",
          marginTop: "30px",
          opacity: isSubmitting ? 0.7 : 1,
        }}
        onClick={handleUpdatePassword}
      >
        <p className="medium-text" style={{ color: "#ffffff" }}>
          {isSubmitting ? "변경 중..." : "변경하기"}
        </p>
      </div>
    </Box>
  );
};

const Box = styled.div`
  width: auto;
  height: 646px;
  padding: 30px;
  border-radius: 20px;
  background-color: #ffffff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
`;
const InputContainer = styled.div`
  width: 500px;
  height: 55px;
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px
    ${(props) => (props.$hasError ? "#f25c5c" : "#e4e4e4")};
  display: flex;
  justify-content: center;
  align-items: center;
  > input {
    width: 470px;
    height: 23px;
    border: none;
    color: #222222;
    &::placeholder {
      color: #888888;
    }
    &:focus {
      outline: none;
    }
  }
`;

const ResetPassword = ({ handleClickBack, handleClickX }) => {
  return (
    <LayoutModal
      Modal={
        <ResetPasswordBox
          handleClickBack={handleClickBack}
          handleClickX={handleClickX}
        />
      }
    />
  );
};

export default ResetPassword;
